import React, { useState } from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, X } from "lucide-react";
import { CONTACT, NAV, IMAGES } from "@/config/content";
import { scrollToId } from "@/lib/scroll";

const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    intro: "Straight Sight is committed to protecting the privacy of all students, parents, and school staff. We comply with the Australian Privacy Principles under the Privacy Act 1988 (Cth).",
    sections: [
      { heading: "1. What information we collect", text: "We collect only the information needed to provide vision care, such as:", list: [
        "Student's name, date of birth, and school year",
        "Parent/guardian contact details",
        "Medical and vision history relevant to the eye assessment",
        "Results of our eye and vision assessments",
      ] },
      { heading: "2. How we store information", text: "All records are stored securely in Zanda (PowerDiary), our encrypted health record system. Access is restricted to authorised Straight Sight clinicians and administration staff. Physical documents (e.g., permission slips) are stored securely and scanned into our system. In line with health record regulations, all medical records are retained for a minimum of 7 years." },
      { heading: "3. How we use and share information", text: "Information is used only for the purpose of providing eye care.", list: [
        "Parents/guardians receive a report about their child's results.",
        "Class teachers receive a summary of students in their class who may need support.",
        "School principals receive an overview of the prevalence of concerns in each grade.",
        "Information is not shared with third parties without parental consent, unless required by law.",
      ] },
      { heading: "4. Security of information", text: "All digital records are encrypted and backed up. Staff are trained in confidentiality and privacy obligations. Equipment and data are sanitised and protected between use." },
      { heading: "5. Your rights", text: "Parents/guardians can request access to their child's information, or ask for corrections, at any time by contacting us." },
    ],
    contact: [
      "Straight Sight Team",
      "Shop 2, 96-98 Columbine Avenue, Punchbowl NSW 2196",
      "admin@straightsight.com.au",
      "www.straightsight.com.au",
      "0433 581 704 · 0400 630 986",
    ],
  },
  terms: { title: "Terms of Use", placeholder: true, body: "Placeholder for legal review. These terms will govern the use of this website. Website information is general in nature. Please replace this placeholder with reviewed Terms of Use before launch." },
  accessibility: { title: "Accessibility", body: "Straight Sight aims to meet WCAG 2.2 AA principles. This site supports keyboard navigation, visible focus states, reduced-motion preferences and accessible colour contrast. If you experience any accessibility barrier, please contact us and we will assist." },
  disclaimer: { title: "Clinical Disclaimer", body: "Website information is general in nature and does not replace individual professional assessment or medical advice. Eligibility for bulk billing and free glasses is subject to conditions. No treatment outcome is guaranteed. Always seek advice from a qualified eye-care professional regarding an individual's vision." },
};

export const Footer = () => {
  const [modal, setModal] = useState(null);
  const year = new Date().getFullYear();

  return (
    <footer className="bg-brand-navy text-white pt-16 pb-8">
      <div className="max-site container-px">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <div className="mb-4 inline-flex bg-white rounded-2xl px-4 py-3 soft-shadow">
              <img src={IMAGES.logo} alt="Straight Sight — our focus is your vision" className="h-9 w-auto" />
            </div>
            <p className="text-sm text-white/70 leading-relaxed">Comprehensive vision assessments delivered directly to schools and childcare centres by experienced Orthoptists and Optometrists.</p>
            <div className="flex gap-3 mt-5">
              {[["facebook", Facebook], ["instagram", Instagram], ["linkedin", Linkedin]]
                .filter(([k]) => CONTACT.social[k] && CONTACT.social[k] !== "#")
                .map(([k, Ic]) => (
                <a key={k} href={CONTACT.social[k]} target="_blank" rel="noopener noreferrer" aria-label={`Straight Sight on ${k}`} data-testid={`social-${k}`} className="grid place-items-center w-9 h-9 rounded-xl bg-white/10 hover:bg-brand-teal transition-colors focus-ring"><Ic className="w-4 h-4" /></a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="!text-white font-bold mb-4">Explore</h4>
            <ul className="space-y-2.5">
              {NAV.map((n) => <li key={n.id}><button onClick={() => scrollToId(n.id)} className="text-sm text-white/70 hover:text-brand-sky transition-colors focus-ring">{n.label}</button></li>)}
            </ul>
          </div>

          <div>
            <h4 className="!text-white font-bold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex gap-3"><MapPin className="w-4 h-4 text-brand-sky shrink-0 mt-0.5" /><span>{CONTACT.address}</span></li>
              <li className="flex gap-3"><Phone className="w-4 h-4 text-brand-sky shrink-0" /><a href={`tel:${CONTACT.phoneIntl}`} className="hover:text-brand-sky">{CONTACT.phone}</a></li>
              <li className="flex gap-3"><Mail className="w-4 h-4 text-brand-sky shrink-0" /><a href={`mailto:${CONTACT.email}`} className="hover:text-brand-sky break-all">{CONTACT.email}</a></li>
              {CONTACT.email2 && <li className="flex gap-3"><Mail className="w-4 h-4 text-brand-sky shrink-0" /><a href={`mailto:${CONTACT.email2}`} className="hover:text-brand-sky break-all">{CONTACT.email2}</a></li>}
            </ul>
          </div>

          <div>
            <h4 className="!text-white font-bold mb-4">Legal</h4>
            <ul className="space-y-2.5">
              {Object.entries(LEGAL).map(([k, v]) => <li key={k}><button onClick={() => setModal(k)} data-testid={`legal-${k}`} className="text-sm text-white/70 hover:text-brand-sky transition-colors focus-ring">{v.title}</button></li>)}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10">
          <p className="text-xs text-white/50 leading-relaxed max-w-4xl">Straight Sight provides eye-care and orthoptic services. Website information is general in nature and does not replace individual professional assessment or medical advice.</p>
          <p className="text-xs text-white/40 mt-3">© {year} Straight Sight Pty Ltd. All rights reserved.</p>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-brand-navy/80 backdrop-blur-sm p-6" onClick={() => setModal(null)}>
          <div className="relative bg-white text-brand-ink rounded-3xl max-w-lg w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="legal-modal">
            <h3 className="font-extrabold text-xl text-brand-navy">{LEGAL[modal].title}</h3>
            {LEGAL[modal].placeholder && (
              <span className="inline-block text-[10px] font-bold uppercase tracking-wide text-brand-amber bg-brand-amber/10 rounded-full px-2.5 py-1 mt-2">Placeholder — pending legal review</span>
            )}
            {LEGAL[modal].sections ? (
              <div className="mt-4 space-y-5">
                <p className="text-sm leading-relaxed text-brand-ink/80">{LEGAL[modal].intro}</p>
                {LEGAL[modal].sections.map((s) => (
                  <div key={s.heading}>
                    <h4 className="font-bold text-brand-navy text-sm">{s.heading}</h4>
                    {s.text && <p className="mt-1 text-sm leading-relaxed text-brand-ink/80">{s.text}</p>}
                    {s.list && (
                      <ul className="mt-2 space-y-1.5">
                        {s.list.map((li) => (
                          <li key={li} className="flex items-start gap-2 text-sm text-brand-ink/80"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-teal shrink-0" />{li}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {LEGAL[modal].contact && (
                  <div className="rounded-2xl bg-brand-bg border border-brand-navy/8 p-4 text-sm text-brand-ink/80">
                    {LEGAL[modal].contact.map((c, i) => (
                      <p key={c} className={i === 0 ? "font-bold text-brand-navy" : "mt-0.5"}>{c}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-4 text-sm leading-relaxed text-brand-ink/80">{LEGAL[modal].body}</p>
            )}
            <button onClick={() => setModal(null)} className="absolute top-5 right-5 grid place-items-center w-9 h-9 rounded-full bg-brand-bg focus-ring" aria-label="Close"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </footer>
  );
};
